/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * Signup
 *
 */

import {
  Conditional,
  Space,
  Text,
  TwoFactorAuthModal,
  ConfirmModal,
} from '@cashfree-intl/coherent';
import PropTypes from 'prop-types';
import React, { memo, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';

import Helmet from 'components/Helmet';
import EVENTS from 'constants/analytics';
import { AuthContext } from 'contexts/AuthContext';
import Analytics from 'utils/analytics';

import { getMetaData } from 'containers/OnboardingContainer/saga';
import {
  generateReCAPTCHA,
  getItemFromLocalStorage,
  isLocalStorageValueValid,
  removeItemFromLocalStorage,
  sentryCaptureException,
  useQuery,
} from 'utils/common';
import { FRESHSALES_TRACKING_METRICS_PARAMS } from 'utils/constants';
import { EMAIL_NOT_VERIFIED } from 'containers/LandingPage/constants';
import {
  loginSignupErrorCode,
  SIGNUP,
  SIGNUP_TYPE,
  USER_DETAILS_COMPLETED,
} from '../OnboardingContainer/constants';

import {
  checkEmailExistsAction,
  disableLoaderAction,
  enableLoaderAction,
  redeemVoucherCodeAction,
  resendEmailOTPAction,
  sendSignupOTPAction,
  setSignupFirstStepDataAction,
  signupMerchantAction,
  updateFormStatus as updateFormStatusAction,
  updateMerchantDetailsAction,
  verifyEmailOTPAction,
  verifyImportsAction,
} from '../OnboardingContainer/actions';

import MerchantDetailsForm from './components/MerchantDetailsForm';
import UserDetailsForm from './components/UserDetailsForm';

import { trackSignupEvent } from './helper';
import { IS_FIRST_SESSION } from './constants';

const initialOTPModalData = {
  show: false,
  authType: '',
  phone: '',
  email: '',
};

export function Signup(props) {
  const [voucherSuccess, setVoucherSuccess] = useState([]);
  const [voucherFail, setVoucherFail] = useState('');
  const [otpModalData, setOTPModalData] = useState(initialOTPModalData);
  const [signupStep2Data, setSignupStep2Data] = useState({});
  const [showLoginInsteadModal, setShowLoginInsteadModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { latitude, longitude } = useContext(AuthContext);

  const searchParams = new URLSearchParams(location.search);
  const sourceProduct = searchParams.get('source-action') || undefined;

  const {
    enableLoader,
    referrer,
    formName,
    signUpFormStatus,
    userDetails,
    merchantDetails,
    isFormSubmitted,
    switchForm,
    redeemVoucherCode,
    checkEmailExists,
    signupFirstStepData,
    signupMerchant,
    sendSignupOTP,
    disableLoader,
    setSignupFirstStepData,
    updateFormStatus,
    verifyImports,
    verifyEmailOTP,
    resendEmailOTP,
  } = props;

  useEffect(() => {
    /**
     * In case user gets "Step-2 Incomplete" error while logging in, commonauth will
     * redirect user to signup page.
     * In this case signup page should skip step-1 and render the step-2 form of signup where user can enter their phone number and verify it.
     *
     * NOTE: Before redirecting to signup, commonauth would set necessary information in localStorage under `userData` key. This detail
     * can be used to complete the signup.
     */

    try {
      const signupDataSentFromCommonAuth = getItemFromLocalStorage('userData');
      const parsedData = {};
      if (isLocalStorageValueValid(signupDataSentFromCommonAuth)) {
        const userData = JSON.parse(signupDataSentFromCommonAuth);
        parsedData.email = userData.email;
        parsedData.password = userData.password;
        parsedData.signupType = userData.signupType;
        parsedData.googleToken = userData.google_id_token;
        parsedData.deviceId = userData.device_id;
      }

      if (!_isEmpty(parsedData)) {
        // set retrieved data from commonauth to reducer
        setSignupFirstStepData(parsedData);

        // update form status to render step-2 of Signup
        if (signupFirstStepData !== USER_DETAILS_COMPLETED)
          updateFormStatus(USER_DETAILS_COMPLETED);
      }
    } catch (error) {
      console.error(error);
    }
  }, [signUpFormStatus]);

  const redeemVoucher = (values, actions) => {
    Analytics.trackEvent(EVENTS.SIGNUP.STEP_2.REDEEM_NOW, null, 'OB')();

    redeemVoucherCode({
      ...values,
      actions,
      cbSuccess: res => {
        setVoucherSuccess(res);
        Analytics.trackEvent(
          EVENTS.SIGNUP.STEP_2.VOUCHER_RESPONSE,
          {
            response: 'Success',
            promo_code: values.code,
          },
          'OB',
        )();
      },
      cbError: error => {
        setVoucherFail(error);
        Analytics.trackEvent(
          EVENTS.SIGNUP.STEP_2.VOUCHER_RESPONSE,
          {
            response: 'Failure',
            promo_code: values.code,
            error_message: error,
          },
          'OB',
        )();
      },
    });
  };

  /**
   *
   * @param {{email: string, password: string, channel: string, deviceId: string, signupType: string captchaResponse: string, termsId?: string}} data
   */
  const handleContinue = data => {
    /**
     * check if email already exists in DB
     * 1. [ERROR] If exists, show a toast message that says 'Account already exists, login with existing account or use a new Email address' and let user navigate
     *    to login if they want to proceed or else signup with a new email address
     * 2. [SUCCESS] If not, store data locally and take user to Step-2
     */

    checkEmailExists({
      ...data,
      navigate,
      onSuccess: response => {
        trackSignupEvent({
          action: 'validate_email',
          status: 'success',
          signupType: SIGNUP_TYPE.PASSWORD,
          email: data.email || response.email,
        });
      },
      onError: error => {
        if (_get(error, 'title') === 'EMAIL_VERIFICATION_REQUIRED') {
          setOTPModalData({
            show: true,
            authType: 'EMAIL',
            email: _get(error, 'data.email'),
          });
        }
        if (_get(error, 'errorCode') === loginSignupErrorCode.USER_EXISTS) {
          setShowLoginInsteadModal(true);
        }
        trackSignupEvent({
          action: 'validate_email',
          status: 'error',
          message: error?.message,
          signupType: SIGNUP_TYPE.PASSWORD,
          email: data.email || error.data?.email,
        });
      },
    });
  };

  /**
   * @param {{signupType: 'SIGNUP_GOOGLE' | 'SIGNUP_PASSWORD',phone: string, countryCode: string, onSuccess?: Function}} data
   */
  const sendOTPRequestWithCaptcha = data => {
    const { signupType, ...rest } = data;
    generateReCAPTCHA({
      onSuccess: captchaResponse => {
        sendSignupOTP({ ...rest, captchaResponse });
      },
      onError: () => {
        sendSignupOTP({ ...rest, captchaResponse: ' ' });
        disableLoader();
        sentryCaptureException({
          errMsg: new Error(`${signupType}: Failed to load recaptcha`),
          priority: 'p0',
          label: 'auth',
        });
      },
    });
  };

  /**
   * @param {{countryCode: string, phone: string, appType: string, merchantSiteUrl: string, whatsappConsent: string, leadSourceId: number}} data
   */
  const handleVerifyBeforeCreateAccount = data => {
    sendOTPRequestWithCaptcha({
      email: signupFirstStepData.email,
      phone: data.phone,
      countryCode: data.countryCode,
      signupType: signupFirstStepData.signupType,
      onSuccess: response => {
        // show OTP modal
        setSignupStep2Data(data);
        setOTPModalData({ ...response, show: true, authType: 'PHONE' });
        trackSignupEvent({
          action: 'send_otp',
          status: 'success',
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
          phone: data.phone,
        });
      },
      onError: error => {
        trackSignupEvent({
          action: 'send_otp',
          status: 'error',
          message: error?.message,
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
          phone: data.phone,
        });
      },
    });
  };

  const getFreshSalesMetrics = () => {
    const freshSalesMetricsObj = { ...FRESHSALES_TRACKING_METRICS_PARAMS };
    Object.keys(freshSalesMetricsObj).forEach(key => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const value = useQuery().get(key);
      if (value) freshSalesMetricsObj[key] = value;
    });

    return freshSalesMetricsObj;
  };

  /**
   * Since this function triggers from the CTA of OTP modal, it must return Promise
   * with resolved value "SUCCESS"  or "ERROR" for appropriate handling of modal visibility.
   *
   * @param {string} otp
   * @returns {Promise<'SUCCESS' | 'ERROR'>}
   */
  const handleCreateAccount = otp =>
    new Promise(resolve => {
      const freshSalesMetricsObj = getFreshSalesMetrics();

      // append metadata and other metrics data fetched from query param
      const signupSecondStepData = {
        ...signupStep2Data,
        ...freshSalesMetricsObj,
        cf_action_type: freshSalesMetricsObj.action,
        cf_cta_button: freshSalesMetricsObj['button-id'],
        metadata: getMetaData(),
      };

      const signupWithRecaptcha = captchaResponse => {
        signupMerchant({
          ...signupFirstStepData,
          ...signupSecondStepData,
          otp,
          latitude,
          longitude,
          captchaResponse,
          sourceProduct,
          onSuccess: () => {
            /**
             * remove `userData` field from localStorage if it exists.
             *
             * NOTE: `userData` variable in LocalStorage is set by common-auth login page in case of `Step-2 incomplete` error. This variable
             * contains required data of user to complete signup process.
             * Hence once signup completes, remove this variable from localStorage.
             */
            removeItemFromLocalStorage('userData');
            sessionStorage.setItem(IS_FIRST_SESSION, 'true');
            if (sourceProduct)
              sessionStorage.setItem('signupSource', sourceProduct);

            resolve('SUCCESS');
            trackSignupEvent({
              action: 'signup_complete',
              status: 'success',
              signupType: signupFirstStepData.signupType,
              latitude,
              longitude,
              locationPermissionGranted: !!latitude,
              email: signupFirstStepData.email,
              phone: signupStep2Data.phone,
            });
          },
          onError: error => {
            resolve('ERROR');

            if (error.title === EMAIL_NOT_VERIFIED) {
              // move back to step-1
              setOTPModalData({ ...initialOTPModalData });
            }
            trackSignupEvent({
              action: 'signup_complete',
              status: 'error',
              message: error?.message,
              signupType: signupFirstStepData.signupType,
              latitude,
              longitude,
              locationPermissionGranted: !!latitude,
              email: signupFirstStepData.email,
              phone: signupStep2Data.phone,
            });
          },
        });
      };

      generateReCAPTCHA({
        onSuccess: signupWithRecaptcha,
        onError: () => {
          signupWithRecaptcha(' ');
          disableLoader();
          sentryCaptureException({
            errMsg: new Error(
              `${signupFirstStepData.signupType}: Failed to load recaptcha`,
            ),
            priority: 'p0',
            label: 'auth',
          });
        },
      });
    });

  const closeOTPModal = () => {
    setOTPModalData(initialOTPModalData);
    disableLoader();
  };

  const resendOTP = () => {
    sendOTPRequestWithCaptcha({
      email: signupFirstStepData.email,
      phone: signupStep2Data.phone,
      countryCode: signupStep2Data.countryCode,
      signupType: signupFirstStepData.signupType,
      onSuccess: () => {
        trackSignupEvent({
          action: 'resend_otp',
          status: 'success',
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
          phone: signupStep2Data.phone,
        });
      },
      onError: error => {
        trackSignupEvent({
          action: 'resend_otp',
          status: 'error',
          message: error?.message,
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
          phone: signupStep2Data.phone,
        });
      },
    });
  };

  console.log('signUpFormStatus', signUpFormStatus);
  const handleVerifyForImports = data => {
    verifyImports(data);
  };

  const handleVerifyEmailOtp = async otp => {
    const captchaResponse = await new Promise(resolve =>
      generateReCAPTCHA({
        onSuccess: captcha => resolve(captcha),
        onError: () => resolve(' '),
      }),
    );

    verifyEmailOTP({
      email: signupFirstStepData.email,
      otp,
      captchaResponse,
      onSuccess: response => {
        trackSignupEvent({
          action: 'verify_email_otp',
          status: 'success',
          message: response?.message,
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
        });
      },
      onError: error => {
        trackSignupEvent({
          action: 'verify_email_otp',
          status: 'error',
          message: error?.message,
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
        });
      },
    });
  };

  const handleResendEmailOTP = async () => {
    const captchaResponse = await new Promise(resolve =>
      generateReCAPTCHA({
        onSuccess: captcha => resolve(captcha),
        onError: () => resolve(' '),
      }),
    );

    resendEmailOTP({
      email: signupFirstStepData.email,
      captchaResponse,
      onSuccess: response => {
        trackSignupEvent({
          action: 'resend_email_otp',
          status: 'success',
          message: response?.message,
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
        });
      },
      onError: error => {
        trackSignupEvent({
          action: 'resend_email_otp',
          status: 'error',
          message: error?.message,
          signupType: signupFirstStepData.signupType,
          email: signupFirstStepData.email,
        });
      },
    });
  };

  const onConfirmLoginInstead = () => {
    sessionStorage.setItem('email', signupFirstStepData.email);
    sessionStorage.setItem('identity', signupFirstStepData.password);
    setShowLoginInsteadModal(false);
    switchForm();
  };

  return (
    <div className="signup-container">
      <Helmet
        title="Sign up with Cashfree"
        content="Sign up now to accept and disburse payments instantly using Cashfree's Payment Gateway & Payouts. We support Netbanking, Credit / Debit Cards, UPI, Wallets, International Cards & EMI"
      />
      <Space
        justifyContent="center"
        className={
          signUpFormStatus === USER_DETAILS_COMPLETED
            ? 'mr-btm-24'
            : 'mr-btm-32 container-header'
        }
      >
        <Text
          className={`header text-center ${
            signUpFormStatus === USER_DETAILS_COMPLETED ? 'signup' : ''
          }`}
        >
          {signUpFormStatus === USER_DETAILS_COMPLETED
            ? 'Create your Account.'
            : 'Sign Up'}
        </Text>
        <Conditional if={signUpFormStatus !== USER_DETAILS_COMPLETED}>
          <div id="switchForm">
            <Text variant="b12" className="user-select-none">
              <span className="text-light">
                {formName === SIGNUP
                  ? 'Have an account?'
                  : "Don't have an account?"}
              </span>{' '}
              <span
                className="text-primary font-bold pointer"
                onClick={switchForm}
              >
                {formName === SIGNUP ? 'Log In' : 'Sign Up'}
              </span>
            </Text>
          </div>
        </Conditional>
      </Space>
      {signUpFormStatus === USER_DETAILS_COMPLETED ? (
        <MerchantDetailsForm
          merchantDetails={merchantDetails}
          referrer={referrer}
          enableLoader={enableLoader}
          onCreateAccount={handleVerifyBeforeCreateAccount}
          verifyAccountForImports={handleVerifyForImports}
          isFormSubmitted={isFormSubmitted}
          redeemVoucher={redeemVoucher}
          voucherFail={voucherFail}
          voucherSuccess={voucherSuccess}
          setVoucherSuccess={setVoucherSuccess}
          setVoucherFail={setVoucherFail}
        />
      ) : (
        <UserDetailsForm
          userDetails={userDetails}
          referrer={referrer}
          enableLoader={enableLoader}
          disableLoader={disableLoader}
          onContinue={handleContinue}
        />
      )}
      {otpModalData.show && otpModalData.authType === 'PHONE' && (
        <TwoFactorAuthModal
          open={otpModalData.show}
          type="OTP"
          infoLabel={
            <>
              Enter the 6-digit OTP sent to your phone number{' '}
              <strong>{otpModalData.phone}</strong>
            </>
          }
          authInfo={{
            timerInSeconds: 10,
          }}
          onConfirm={handleCreateAccount}
          onClose={closeOTPModal}
          onResend={resendOTP}
        />
      )}
      {otpModalData.show && otpModalData.authType === 'EMAIL' && (
        <TwoFactorAuthModal
          title="Verify email"
          open={otpModalData.show}
          type="OTP"
          infoLabel={
            <>
              Enter the 6-digit OTP sent to your email{' '}
              <Text variant="b14" strong>
                {otpModalData.email}
              </Text>
            </>
          }
          authInfo={{
            timerInSeconds: 10,
          }}
          onConfirm={handleVerifyEmailOtp}
          onClose={closeOTPModal}
          onResend={handleResendEmailOTP}
        />
      )}
      <Conditional if={showLoginInsteadModal}>
        <ConfirmModal
          title="Account Already Exists"
          confirmText="Login"
          cancelText="Enter different email"
          onClose={() => setShowLoginInsteadModal(false)}
          onConfirm={onConfirmLoginInstead}
        >
          Would you like to login instead?
        </ConfirmModal>
      </Conditional>
    </div>
  );
}

Signup.propTypes = {
  enableLoader: PropTypes.func.isRequired,
  referrer: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  signUpFormStatus: PropTypes.string.isRequired,
  userDetails: PropTypes.object.isRequired,
  signupFirstStepData: PropTypes.object.isRequired,
  merchantDetails: PropTypes.object.isRequired,
  isFormSubmitted: PropTypes.bool.isRequired,
  switchForm: PropTypes.func.isRequired,
  redeemVoucherCode: PropTypes.func.isRequired,
  checkEmailExists: PropTypes.func.isRequired,
  signupMerchant: PropTypes.func.isRequired,
  sendSignupOTP: PropTypes.func.isRequired,
  disableLoader: PropTypes.func.isRequired,
  setSignupFirstStepData: PropTypes.func.isRequired,
  updateFormStatus: PropTypes.func.isRequired,
  verifyImports: PropTypes.func,
  verifyEmailOTP: PropTypes.func,
  resendEmailOTP: PropTypes.func,
};

function mapDispatchToProps(dispatch) {
  return {
    enableLoader: () => dispatch(enableLoaderAction()),
    disableLoader: () => dispatch(disableLoaderAction()),
    updateMerchantDetails: data => dispatch(updateMerchantDetailsAction(data)),
    redeemVoucherCode: data => dispatch(redeemVoucherCodeAction(data)),
    checkEmailExists: data => dispatch(checkEmailExistsAction(data)),
    signupMerchant: data => dispatch(signupMerchantAction(data)),
    sendSignupOTP: data => dispatch(sendSignupOTPAction(data)),
    setSignupFirstStepData: data =>
      dispatch(setSignupFirstStepDataAction(data)),
    updateFormStatus: data => dispatch(updateFormStatusAction(data)),
    verifyImports: data => dispatch(verifyImportsAction(data)),
    verifyEmailOTP: data => dispatch(verifyEmailOTPAction(data)),
    resendEmailOTP: data => dispatch(resendEmailOTPAction(data)),
  };
}

const withConnect = connect(
  null,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Signup);
